import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { AddOneOffSessionData } from './AddOneOffSessionDialog';
import { NO_REPORT_IMPACT_TRACKED, NO_REPORT_NO_IMPACT } from '../../activity-management/activity-form-v2/activity-form-actions-v2';

interface AddOneOffSessionResponse {
  id: string;
}

export const useAddOneOffSession = () => {
  const apiUrl = useApiUrl();
  const addOneOffSessionFetch = useCampfireFetch({
    defer: true,
  });

  const addOneOffSession = (sessionData: AddOneOffSessionData) => {
    return addOneOffSessionFetch.run({
      url: `${apiUrl}/vm/session/one-off/save`,
      method: 'POST',
      data: sessionData,
    });
  };

  return {
    addOneOffSession,
    isLoading: addOneOffSessionFetch.isLoading,
    status: addOneOffSessionFetch.status,
    response: addOneOffSessionFetch.response,
  };
};

export const useAddBulkOneOffSessions = () => {
  const apiUrl = useApiUrl();
  const addBulkOneOffSessionsFetch = useCampfireFetch({
    defer: true,
  });

  const addBulkOneOffSessions = (activityId: string, sessions: Omit<AddOneOffSessionData, 'activityId'>[]) =>
    addBulkOneOffSessionsFetch.run({
      url: `${apiUrl}/vm/session/one-off/save-bulk`,
      method: 'POST',
      data: { activityId, sessions },
    });

  return {
    addBulkOneOffSessions,
    isLoading: addBulkOneOffSessionsFetch.isLoading,
  };
};

// Delete one-off session hook
export const useDeleteOneOffSession = () => {
  const apiUrl = useApiUrl();
  const deleteOneOffSessionFetch = useCampfireFetch({
    defer: true,
  });

  const deleteOneOffSession = (sessionId: string) => {
    return deleteOneOffSessionFetch.run({
      url: `${apiUrl}/vm/session/one-off/delete`,
      method: 'POST',
      data: { sessionId },
    });
  };

  return {
    deleteOneOffSession,
    isLoading: deleteOneOffSessionFetch.isLoading,
    status: deleteOneOffSessionFetch.status,
    response: deleteOneOffSessionFetch.response,
  };
};

// Edit one-off session hook
export const useEditOneOffSession = () => {
  const apiUrl = useApiUrl();
  const editOneOffSessionFetch = useCampfireFetch({
    defer: true,
  });

  const editOneOffSession = (sessionData: Partial<AddOneOffSessionData>) => {
    return editOneOffSessionFetch.run({
      url: `${apiUrl}/vm/session/one-off/save`,
      method: 'POST',
      data: sessionData,
    });
  };

  return {
    editOneOffSession,
    isLoading: editOneOffSessionFetch.isLoading,
    status: editOneOffSessionFetch.status,
    response: editOneOffSessionFetch.response,
  };
};

export const addOneOffSessionAPI = async (sessionData: AddOneOffSessionData): Promise<string> => {
  const response = await fetch('/fusion/session/one-off/save', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // Add authentication headers if needed
    },
    body: JSON.stringify(sessionData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to create one-off session');
  }

  const result = await response.json();
  return result.id;
};

// Transform form data to API format
export const transformSessionDataForAPI = (formData: AddOneOffSessionData): any => {
  const apiData: any = {
    activityId: formData.activityId,
    name: formData.name,
    description: formData.description || '', // Required field - always include
    activityDate: formData.activityDate,
  };

  // Include optional fields
  if (formData.minVolunteers !== undefined && formData.minVolunteers !== null) {
    apiData.minVolunteers = formData.minVolunteers;
  }

  // Always include maxVolunteers if it's defined (including 0)
  if (formData.maxVolunteers !== undefined) {
    apiData.maxVolunteers = formData.maxVolunteers;
  }

  if (formData.startTime) {
    apiData.startTime = formData.startTime;
  }

  if (formData.endTime) {
    apiData.endTime = formData.endTime;
  }

  // Include reportTypeId - backend expects this field
  if (formData.reportTypeId) {
    apiData.reportTypeId = formData.reportTypeId;
  }

  // Include autoReport flag (boolean, same as regular sessions)
  if (formData.autoReport !== undefined) {
    apiData.autoReport = formData.autoReport;
  }

  // Include location if provided
  if (formData.activityLocation) {
    apiData.activityLocation = {
      placesAddress: {
        description: formData.activityLocation.description,
        formatted: formData.activityLocation.formatted,
        placesId: formData.activityLocation.placesId,
        latitude: formData.activityLocation.latitude,
        longitude: formData.activityLocation.longitude,
      },
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      comments: '',
    };
  }

  return apiData;
};

// Transform session data for editing - converts from session object to form data
export const transformSessionDataForEdit = (session: any): any => {
  const editData: any = {
    sessionName: session.name || '',
    sessionDescription: session.description || '',
    minVolunteers: session.minVolunteers || 1,
    maxVolunteers: session.maxVolunteers || null,
    selectedReportTypeId: session.reportType?.reportTypeId 
      ? session.reportType.reportTypeId 
      : (session.autoReport ? NO_REPORT_IMPACT_TRACKED : NO_REPORT_NO_IMPACT),
    sessionLocation: session.activityLocation ? {
      description: session.activityLocation.description,
      formatted: session.activityLocation.formatted,
      latitude: session.activityLocation.latitude,
      longitude: session.activityLocation.longitude,
      placesId: session.activityLocation.placesId,
    } : null,
  };

  // Convert time strings to Date objects for the form
  if (session.startTime) {
    // Assuming startTime is in format "HH:mm"
    const today = new Date();
    const [hours, minutes] = session.startTime.split(':');
    today.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
    editData.sessionStartTime = today;
  }

  if (session.endTime) {
    // Assuming endTime is in format "HH:mm"
    const today = new Date();
    const [hours, minutes] = session.endTime.split(':');
    today.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
    editData.sessionEndTime = today;
  }

  return editData;
};